/*
  GreenOx – Wunschlieferdatum

  Ein Termin je Bestellung, gespeichert als Cart-Attribut `delivery_date` (TT.MM.JJJJ).
  Alle Instanzen auf der Seite teilen denselben Wert und aktualisieren sich gegenseitig.

  Bewusste Entscheidungen:
  - Die Terminberechnung läuft hier statt in Liquid, weil Shopify Seiten cached und ein
    serverseitig gerendertes Datum dann veraltet.
  - Kein localStorage. Der Warenkorb ist die einzige Quelle; ein Backup-Speicher hat in
    der Vorgängerlösung Termine aus alten Sitzungen in neue Bestellungen geschrieben.
  - Das Altattribut `DeliveryDate` wird beim ersten Schreiben geleert, damit nie zwei
    widersprüchliche Werte in derselben Bestellung landen.
*/

(function () {
  'use strict';

  var DOW_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  var DOW_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  var MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  var ATTR = 'delivery_date';
  var LEGACY_ATTR = 'DeliveryDate';
  /*
    Merkt, ob der Termin vom Kunden stammt oder vom System gesetzt wurde. Der
    Unterstrich macht das Attribut zu einem privaten: Shopify blendet es im
    Checkout und in der Bestellbestätigung aus, in der Bestellung selbst ist es
    für die Logistik lesbar. Ein gewählter Termin ist eine Zusage, ein gesetzter
    nur eine Planung — bei Engpässen darf man Letztere verschieben.
  */
  var AUTO_ATTR = '_delivery_date_auto';
  var EVENT = 'gox:lieferdatum';

  var state = { value: null, auto: true, loaded: false, touched: false };

  /*
    Ob die Auswahl aufgeklappt ist, steht hier und nicht nur im DOM: der
    Cart-Drawer wird nach jeder Terminwahl serverseitig neu gerendert, und
    frisches HTML kommt immer zugeklappt zurueck. Getrennt nach Einbaustelle,
    damit ein offener Warenkorb nicht die Buy Box aufklappt.
  */
  var openState = { cart: false, buybox: false };

  /*
    Alle Schreibzugriffe auf den Warenkorb laufen seriell. Sonst überholt der
    Startabgleich eine Auswahl, die der Kunde direkt nach dem Laden trifft, und
    setzt sie wieder zurück.
  */
  var queue = Promise.resolve();
  function enqueue(fn) {
    queue = queue.then(fn, fn);
    return queue;
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function fmt(d) {
    return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
  }

  function fmtShort(d) {
    return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.';
  }

  function parse(str) {
    var m = String(str || '').trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!m) return null;
    var d = new Date(+m[3], +m[2] - 1, +m[1]);
    return d.getDate() === +m[1] && d.getMonth() === +m[2] - 1 ? d : null;
  }

  function addDays(d, n) {
    var x = new Date(d.getTime());
    x.setDate(x.getDate() + n);
    return x;
  }

  function midnight(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function sameDay(a, b) {
    return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  /* Aktuelle Zeit in Europe/Berlin — unabhängig von der Zeitzone des Besuchers. */
  function berlinNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
  }

  /*
    "HH:MM" als Minuten seit Mitternacht. "24:00" ergibt 1440 und liegt damit
    über jeder möglichen Uhrzeit — so lässt sich eine Regel für den ganzen Tag
    einstellen, ohne die Vergleiche zu verbiegen.
  */
  function parseTime(str, fallback) {
    var m = String(str || '').trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return fallback;
    return (+m[1]) * 60 + (+m[2]);
  }

  function readConfig(host) {
    var days = (host.dataset.days || '')
      .split(',')
      .filter(function (v) { return v !== ''; })
      .map(Number);
    var nowahl = (host.dataset.nowahl || '')
      .split(',')
      .filter(function (v) { return v !== ''; })
      .map(Number);
    var blocked = {};
    (host.dataset.blocked || '').split(',').forEach(function (raw) {
      var d = parse(raw);
      if (d) blocked[fmt(d)] = true;
    });
    var cut = parseTime(host.dataset.cutoff, 12 * 60);
    return {
      active: host.dataset.active === 'true',
      days: days.length ? days : [2, 3, 4, 5],
      nowahl: nowahl,
      lead: parseInt(host.dataset.lead, 10) || 2,
      cutoffMin: cut,
      /* Leer heißt "wie Bestellschluss" — dann gibt es nur eine Uhrzeit zu pflegen. */
      vorwahlMin: parseTime(host.dataset.vorwahlBis, cut),
      vorwahlVor: parseInt(host.dataset.vorwahlVor, 10) || 1,
      vorwahlNach: parseInt(host.dataset.vorwahlNach, 10) || 2,
      horizon: parseInt(host.dataset.horizon, 10) || 6,
      blocked: blocked,
      window: host.dataset.window || '',
      countdown: host.dataset.countdown === 'true',
      items: parseInt(host.dataset.items, 10) || 0,
      cart: host.dataset.variant === 'cart'
    };
  }

  /* Wird an diesem Tag überhaupt zugestellt? Gilt auch für den Standardweg. */
  function isDeliverable(d, cfg) {
    return cfg.days.indexOf(d.getDay()) !== -1 && !cfg.blocked[fmt(d)];
  }

  /*
    Darf der Kunde diesen Tag selbst festlegen? Manche Zustelltage sind durch
    Bestellungen ohne Wunschtermin schon ausgelastet — sie bleiben lieferbar
    (und über "Schnellstmöglich" erreichbar), erscheinen aber nicht zur Wahl.
  */
  function isPickable(d, cfg) {
    return isDeliverable(d, cfg) && cfg.nowahl.indexOf(d.getDay()) === -1;
  }

  function afterTime(now, minutes) {
    return now.getHours() * 60 + now.getMinutes() >= minutes;
  }

  function afterCutoff(now, cfg) {
    return afterTime(now, cfg.cutoffMin);
  }

  /* Frühester wählbarer Wunschtermin: Vorlaufzeit ab heute, nach Bestellschluss ab morgen. */
  function earliest(now, cfg) {
    var start = midnight(now);
    if (afterCutoff(now, cfg)) start = addDays(start, 1);
    return addDays(start, cfg.lead);
  }

  function latest(now, cfg) {
    return addDays(midnight(now), cfg.horizon * 7);
  }

  /*
    Voraussichtliche Lieferung ohne Wunschtermin — der Tag, der in der Buy Box
    steht und ohne eigene Wahl in die Bestellung geht.

    Der Abstand ist einstellbar und kippt an der Umschaltzeit: davor der kürzere
    Vorlauf, danach der längere. Steht die Umschaltzeit auf 24:00, gilt den
    ganzen Tag derselbe Abstand.

    Der so errechnete Tag ist ein Wunsch, kein Zustelltag: fällt er auf einen
    Wochentag ohne Zustellung oder auf einen Sperrtag, rückt der nächste
    Zustelltag nach. Genau hier entstehen die Häufungen — sind nur wenige
    Wochentage als Zustelltage aktiv, laufen mehrere Bestelltage auf denselben
    Liefertag zu.
  */
  function standard(now, cfg) {
    var d = addDays(midnight(now), afterTime(now, cfg.vorwahlMin) ? cfg.vorwahlNach : cfg.vorwahlVor);
    for (var i = 0; i < 21 && !isDeliverable(d, cfg); i++) d = addDays(d, 1);
    return d;
  }

  function slots(now, cfg, count) {
    var first = earliest(now, cfg);
    var last = latest(now, cfg);
    var out = [];
    var d = first;
    while (d <= last && out.length < count) {
      if (isPickable(d, cfg)) out.push(d);
      d = addDays(d, 1);
    }
    return out;
  }

  function selectable(d, now, cfg) {
    return isPickable(d, cfg) && d >= earliest(now, cfg) && d <= latest(now, cfg);
  }

  /* ---- Warenkorb ---- */

  function fetchCart() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function writeCart(value, auto) {
    var attributes = {};
    attributes[ATTR] = value || '';
    attributes[AUTO_ATTR] = value && auto ? '1' : '';
    attributes[LEGACY_ATTR] = '';
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) { return r.json(); });
  }

  /* Konfiguration der ersten Instanz — sie ist auf allen Einbaustellen dieselbe. */
  function anyConfig() {
    var host = document.querySelector('gox-lieferdatum');
    return host ? readConfig(host) : null;
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { value: state.value } }));
  }

  /*
    Setzt den Termin. `null` heißt nicht "kein Termin", sondern "zurück auf den
    schnellstmöglichen": In der Bestellung steht immer ein Datum, sonst müsste
    jede nachgelagerte Stelle dieselbe Rechnung noch einmal anstellen.
  */
  function setValue(date) {
    var cfg = anyConfig();
    var auto = !date;
    var d = date || (cfg ? standard(berlinNow(), cfg) : null);
    var next = d ? fmt(d) : '';

    var hosts = document.querySelectorAll('gox-lieferdatum');
    Array.prototype.forEach.call(hosts, function (h) { h.classList.add('is-busy'); });
    state.touched = true;

    return enqueue(function () {
      return writeCart(next, auto)
        .then(function (cart) {
          var attrs = (cart && cart.attributes) || {};
          state.value = attrs[ATTR] || null;
          state.auto = attrs[AUTO_ATTR] === '1';
        })
        .catch(function () {
          /* Netzwerkfehler: Anzeige auf den letzten bestätigten Stand zurücksetzen. */
        })
        .then(function () {
          broadcast();
          Array.prototype.forEach.call(hosts, function (h) { h.classList.remove('is-busy'); });
        });
    });
  }

  /*
    Abgleich beim Laden. Stellt sicher, dass im Warenkorb immer ein gültiges
    Lieferdatum steht:

    - Ein selbst gewählter Termin bleibt, solange er wählbar ist. Fällt er weg
      (Datum vergangen, Zustelltag abgeschaltet, Sperrtag), rückt der
      schnellstmögliche nach statt gar keinem.
    - Ein automatisch gesetzter wird nachgezogen, sobald sich der
      schnellstmögliche verschiebt — etwa wenn ein Warenkorb über Nacht liegen
      bleibt oder der Bestellschluss vorbeigeht.
    - Das Altattribut `DeliveryDate` wird übernommen und geleert.
  */
  function sync() {
    if (state.loaded) return Promise.resolve();
    state.loaded = true;

    var cfg = anyConfig();
    if (!cfg) return Promise.resolve();

    return enqueue(function () {
      return fetchCart()
        .then(function (cart) {
          /* Hat der Kunde inzwischen selbst gewählt, gilt seine Wahl. */
          if (state.touched) return null;

          var attrs = (cart && cart.attributes) || {};
          var raw = attrs[ATTR] || attrs[LEGACY_ATTR] || '';
          var date = parse(raw);
          var now = berlinNow();
          var std = standard(now, cfg);
          /* Ohne Marker gilt ein vorhandenes Datum als selbst gewählt (Altbestand). */
          var warAuto = attrs[AUTO_ATTR] === '1' || !date;
          var behalten = date && !warAuto && selectable(date, now, cfg);

          state.value = fmt(behalten ? date : std);
          state.auto = !behalten;

          /* Nur schreiben, wenn sich am Warenkorb tatsächlich etwas ändert. */
          var mussSchreiben =
            attrs[ATTR] !== state.value ||
            (attrs[AUTO_ATTR] === '1') !== state.auto ||
            !!attrs[LEGACY_ATTR];

          return mussSchreiben ? writeCart(state.value, state.auto) : null;
        })
        .catch(function () { /* Anzeige bleibt auf dem letzten Stand. */ })
        .then(function () { broadcast(); });
    });
  }

  /* ---- Komponente ---- */

  function Instance(host) {
    this.host = host;
    this.cfg = readConfig(host);
    this.head = host.querySelector('.gox-ld__head');
    this.label = host.querySelector('.gox-ld__label');
    this.picker = host.querySelector('.gox-ld__picker');
    this.modes = host.querySelector('.gox-ld__modes');
    this.chips = host.querySelector('.gox-ld__chips');
    this.cal = host.querySelector('.gox-ld__cal');
    this.calGrid = host.querySelector('.gox-ld__cal-grid');
    this.calMonth = host.querySelector('.gox-ld__cal-month');
    this.countdown = host.querySelector('.gox-ld__countdown');
    this.calCursor = null;
    this.timer = null;
    /* 'sofort' = keine Wahl getroffen, 'wunsch' = Tage sind aufgeklappt */
    this.mode = 'sofort';

    if (!this.cfg.active) {
      host.classList.add('gox-ld--static');
      this.picker.hidden = true;
    }

    /*
      Die Kopfzeile ist der Aufklapper: sichtbar ist nur der Liefertag.

      Der offene Zustand liegt in openState statt nur im DOM, weil der
      Cart-Drawer nach jeder Terminwahl serverseitig neu gerendert wird
      (?section_id=…cart-drawer). Ohne das Gedaechtnis kam frisches HTML mit
      aria-expanded="false" zurueck und die Auswahl klappte mitten im Waehlen zu.
    */
    if (this.head && this.head.tagName === 'BUTTON') {
      var stelle = this.cfg.cart ? 'cart' : 'buybox';

      if (openState[stelle]) {
        host.classList.add('is-open');
        this.head.setAttribute('aria-expanded', 'true');
      }

      /*
        Nur einmal je Knopf binden. Der Drawer verschiebt sein HTML beim
        Neu-Rendern, wodurch disconnected/connectedCallback erneut laufen und
        eine zweite Instanz entsteht — ohne diese Sperre haengt dann ein
        zweiter Klick-Handler am selben Knopf. Beide schalten um, das Ergebnis
        hebt sich auf, und die Auswahl liess sich nicht mehr oeffnen.

        Der Handler kommt bewusst ohne `this` aus, damit er auch dann noch
        richtig arbeitet, wenn die Instanz, die ihn angelegt hat, laengst
        ersetzt wurde.
      */
      var kopf = this.head;
      if (!kopf.dataset.goxBound) {
        kopf.dataset.goxBound = '1';
        kopf.addEventListener('click', function () {
          var open = host.classList.toggle('is-open');
          kopf.setAttribute('aria-expanded', open ? 'true' : 'false');
          openState[stelle] = open;
        });
      }
    }

    Array.prototype.forEach.call(host.querySelectorAll('.gox-ld__cal-nav'), function (btn) {
      if (btn.dataset.goxBound) return;
      btn.dataset.goxBound = '1';
      btn.addEventListener('click', function () {
        /* Immer die aktuelle Instanz fragen, nicht die von der Registrierung. */
        var inst = host.gox;
        if (!inst || !inst.calCursor) return;
        inst.calCursor = new Date(inst.calCursor.getFullYear(), inst.calCursor.getMonth() + parseInt(btn.dataset.dir, 10), 1);
        inst.renderCalendar();
      });
    });

    this.onUpdate = this.render.bind(this);
    document.addEventListener(EVENT, this.onUpdate);

    this.render();
    this.timer = setInterval(this.render.bind(this), 30000);
  }

  /* Nach einer Tageswahl bleibt die Auswahl stehen, nur der Kalender schließt. */
  Instance.prototype.close = function () {
    this.cal.hidden = true;
  };

  Instance.prototype.render = function () {
    var cfg = this.cfg;
    var now = berlinNow();
    /*
      Im Warenkorb steht immer ein Datum — ob es der Kunde gewählt hat, sagt
      der Marker, nicht die Anwesenheit des Werts.
    */
    var chosen = state.auto ? null : parse(state.value);
    var win = cfg.window ? ' <span class="gox-ld__win">(' + cfg.window + ')</span>' : '';
    var date = parse(state.value) || standard(now, cfg);

    /* Eine getroffene Wahl hält den Umschalter auf "Wunschtermin". */
    if (chosen) this.mode = 'wunsch';

    if (cfg.cart) {
      /* "zusammen" nur, wenn es auch etwas zusammenzufassen gibt. */
      var was = cfg.items === 1
        ? 'Dein Artikel kommt am'
        : 'Alle ' + cfg.items + ' Artikel kommen zusammen am';
      this.label.innerHTML = was + ' <strong>' +
        DOW_LONG[date.getDay()] + ', ' + fmtShort(date) + '</strong>' + win;
    } else {
      this.label.innerHTML = 'Lieferung <strong>' + DOW_SHORT[date.getDay()] + ', ' + fmtShort(date) +
        '</strong>' + win;
    }

    this.renderModes(now, chosen);
    this.renderChips(now, chosen);
    if (!this.cal.hidden) this.renderCalendar();
    this.renderCountdown(now);
  };

  Instance.prototype.renderModes = function (now, chosen) {
    if (!this.cfg.active || !this.modes) return;
    var self = this;
    var cfg = this.cfg;
    var std = standard(now, cfg);
    var open = this.mode === 'wunsch';

    this.modes.innerHTML = '';
    [
      {
        key: 'sofort',
        title: 'Schnellstmöglich',
        sub: DOW_SHORT[std.getDay()] + ', ' + fmtShort(std)
      },
      {
        key: 'wunsch',
        title: 'Wunschtermin',
        sub: chosen ? DOW_SHORT[chosen.getDay()] + ', ' + fmtShort(chosen) : 'Tag selbst wählen'
      }
    ].forEach(function (m) {
      var active = (m.key === 'wunsch') === open;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'gox-ld__mode';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', active ? 'true' : 'false');
      b.innerHTML = '<span class="gox-ld__mode-t">' + m.title + '</span>' +
        '<span class="gox-ld__mode-s">' + m.sub + '</span>';
      b.addEventListener('click', function () {
        if (m.key === 'sofort') {
          self.mode = 'sofort';
          self.cal.hidden = true;
          /*
            Immer leeren, nicht nur wenn hier gerade ein Termin bekannt ist:
            Shopify liefert einen frisch geschriebenen Wert erst nach ein paar
            Sekunden zurück, und in der Zwischenzeit sähe es so aus, als gäbe es
            nichts zurückzunehmen. Ein leerer Schreibvorgang schadet nicht.
          */
          setValue(null);
          return;
        }
        self.mode = 'wunsch';
        self.render();
      });
      self.modes.appendChild(b);
    });

    this.chips.hidden = !open;
    if (!open) this.cal.hidden = true;
  };

  Instance.prototype.renderChips = function (now, chosen) {
    if (!this.cfg.active) return;
    var self = this;
    var list = slots(now, this.cfg, 3);
    this.chips.innerHTML = '';

    if (!list.length) {
      var empty = document.createElement('p');
      empty.className = 'gox-ld__empty';
      empty.textContent = 'Zurzeit ist kein Wunschtermin verfügbar. Deine Bestellung geht auf dem schnellsten Weg raus.';
      this.chips.appendChild(empty);
      return;
    }

    list.forEach(function (d) {
      var active = sameDay(chosen, d);
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'gox-ld__chip';
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
      b.innerHTML = '<span class="gox-ld__chip-dow">' + DOW_SHORT[d.getDay()] + '</span>' +
        '<span class="gox-ld__chip-date">' + fmtShort(d) + '</span>';
      b.setAttribute('aria-label', 'Liefertermin wählen: ' + DOW_LONG[d.getDay()] + ', ' + fmt(d));
      b.addEventListener('click', function () {
        setValue(d).then(function () { self.close(); });
      });
      self.chips.appendChild(b);
    });

    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'gox-ld__chip gox-ld__chip--more';
    more.setAttribute('aria-label', 'Anderen Termin im Kalender wählen');
    more.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>' +
      '<span class="gox-ld__chip-more-t">Anderer Tag</span>';
    more.addEventListener('click', function () {
      self.cal.hidden = !self.cal.hidden;
      if (!self.cal.hidden) {
        var base = chosen || list[0];
        self.calCursor = new Date(base.getFullYear(), base.getMonth(), 1);
        self.renderCalendar();
      }
    });
    this.chips.appendChild(more);
  };

  Instance.prototype.renderCalendar = function () {
    var self = this;
    var cfg = this.cfg;
    var now = berlinNow();
    /* Nur ein selbst gewählter Tag wird im Kalender markiert, nicht der gesetzte. */
    var chosen = state.auto ? null : parse(state.value);
    var first = earliest(now, cfg);
    var last = latest(now, cfg);

    if (!this.calCursor) this.calCursor = new Date(first.getFullYear(), first.getMonth(), 1);
    var cur = this.calCursor;
    this.calMonth.textContent = MONTHS[cur.getMonth()] + ' ' + cur.getFullYear();

    this.host.querySelector('.gox-ld__cal-nav[data-dir="-1"]').disabled =
      new Date(cur.getFullYear(), cur.getMonth(), 0) < first;
    this.host.querySelector('.gox-ld__cal-nav[data-dir="1"]').disabled =
      new Date(cur.getFullYear(), cur.getMonth() + 1, 1) > last;

    this.calGrid.innerHTML = '';
    ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].forEach(function (n) {
      var h = document.createElement('span');
      h.className = 'gox-ld__dow';
      h.textContent = n;
      self.calGrid.appendChild(h);
    });

    var offset = (new Date(cur.getFullYear(), cur.getMonth(), 1).getDay() + 6) % 7;
    for (var o = 0; o < offset; o++) {
      var sp = document.createElement('span');
      sp.className = 'gox-ld__day gox-ld__day--empty';
      this.calGrid.appendChild(sp);
    }

    var dim = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
    for (var day = 1; day <= dim; day++) {
      (function (d) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gox-ld__day';
        btn.textContent = d.getDate();
        if (!selectable(d, now, cfg)) {
          btn.disabled = true;
        } else {
          var active = sameDay(chosen, d);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          btn.setAttribute('aria-label', DOW_LONG[d.getDay()] + ', ' + fmt(d));
          btn.addEventListener('click', function () {
            setValue(d).then(function () { self.close(); });
          });
        }
        self.calGrid.appendChild(btn);
      })(new Date(cur.getFullYear(), cur.getMonth(), day));
    }
  };

  Instance.prototype.renderCountdown = function (now) {
    if (!this.cfg.countdown || !this.cfg.active || afterCutoff(now, this.cfg)) {
      this.countdown.hidden = true;
      return;
    }
    var mins = this.cfg.cutoffMin - (now.getHours() * 60 + now.getMinutes());
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    this.countdown.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>' +
      'Noch <strong>' + (h > 0 ? h + ' Std ' : '') + m + ' Min</strong> für den frühesten Liefertermin';
    this.countdown.hidden = false;
  };

  Instance.prototype.destroy = function () {
    document.removeEventListener(EVENT, this.onUpdate);
    if (this.timer) clearInterval(this.timer);
  };

  /* ---- Registrierung ---- */

  if (!window.customElements.get('gox-lieferdatum')) {
    window.customElements.define('gox-lieferdatum', class extends HTMLElement {
      connectedCallback() {
        if (this.gox) return;
        this.gox = new Instance(this);
        sync();
      }
      disconnectedCallback() {
        if (this.gox) {
          this.gox.destroy();
          this.gox = null;
        }
      }
    });
  }
})();
